import { memo } from "react";
import { classNames } from "helpers";

interface SectionCardProps {
  title: string;
  subtitle?: string;
  /** Sadržaj u gornjem desnom kutu sekcije (npr. badge s brojem). */
  action?: React.ReactNode;
  children: React.ReactNode;
}

export const SectionCard: React.FC<SectionCardProps> = ({ title, subtitle, action, children }) => (
  <section className="bg-white border border-slate-200 rounded-sm p-5">
    {/* Naslov lijevo, objašnjenje desno — sekcija tako počinje sadržajem, a ne s dva retka teksta. */}
    <div className="flex items-start justify-between gap-4 mb-3">
      <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">{title}</div>
      <div className="flex items-start gap-3 min-w-0">
        {subtitle && <div className="text-xs text-slate-400 text-right">{subtitle}</div>}
        {action}
      </div>
    </div>
    {children}
  </section>
);

export const ChipGroupTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">{children}</div>
);

interface ChipToggleProps {
  label: string;
  selected: boolean;
  onToggle?: () => void;
  block?: boolean;
  /** Prikaz bez klika — isti izgled opreme na detalju oglasa kao na unosu. */
  readOnly?: boolean;
}

export const ChipToggle: React.FC<ChipToggleProps> = memo(({ label, selected, onToggle, block, readOnly }) => (
  <button
    type="button"
    onClick={readOnly ? undefined : onToggle}
    disabled={readOnly}
    className={classNames(
      "items-center gap-2 px-3 py-2.5 rounded-sm border text-sm transition-colors",
      block ? "flex w-full text-left" : "inline-flex",
      readOnly ? "cursor-default" : "",
      selected
        ? "bg-sky-50 border-sky-500 text-sky-700"
        : classNames("bg-white border-slate-200 text-slate-600", readOnly ? "" : "hover:border-slate-300")
    )}
  >
    <span
      className={classNames(
        "w-4 h-4 rounded border flex items-center justify-center flex-shrink-0",
        selected ? "bg-sky-600 border-sky-600 text-white" : "border-slate-300 bg-white"
      )}
    >
      {selected && (
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
        </svg>
      )}
    </span>
    {label}
  </button>
));

interface SwitchToggleCardProps {
  title: string;
  description?: string;
  checked: boolean;
  onToggle: () => void;
}

export const SwitchToggleCard: React.FC<SwitchToggleCardProps> = memo(({ title, description, checked, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    className={classNames(
      "w-full flex items-center justify-between gap-4 px-4 py-3 border rounded-md text-left transition-colors",
      checked ? "bg-sky-50 border-sky-200" : "bg-white border-slate-200 hover:border-slate-300"
    )}
  >
    <div className="min-w-0">
      <div className={classNames("font-medium text-sm", checked ? "text-sky-700" : "text-slate-700")}>{title}</div>
      {description && <div className="text-xs text-slate-400 mt-0.5">{description}</div>}
    </div>
    <span
      className={classNames(
        "relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors",
        checked ? "bg-sky-600" : "bg-slate-300"
      )}
    >
      <span
        className={classNames(
          "absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform",
          checked ? "translate-x-5" : "translate-x-0"
        )}
      />
    </span>
  </button>
));
